import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SignatureCanvas from "react-signature-canvas";
import {
  CheckCircle2, Loader2, X as IconX, FileText, Download, PenLine, Trash2, Heart,
} from "lucide-react";
import { toast } from "sonner";

export default function PortalParteMontaje() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nombre, setNombre] = useState("");
  const [dni, setDni] = useState("");
  const sigRef = useRef(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get(`/portal-parte/${token}`);
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Enlace no válido o caducado");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const clearSig = () => sigRef.current?.clear();

  const submit = async () => {
    if (!nombre.trim()) { toast.error("Indica tu nombre"); return; }
    if (!sigRef.current || sigRef.current.isEmpty()) { toast.error("Firma con tu dedo en el recuadro"); return; }
    setSaving(true);
    try {
      const firmaBase64 = sigRef.current.getTrimmedCanvas().toDataURL("image/png");
      await api.post(`/portal-parte/${token}/firmar`, {
        firma_base64: firmaBase64,
        nombre: nombre.trim(),
        dni: dni.trim() || null,
      });
      toast.success("¡Gracias por tu conformidad!");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando firma");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-amber-400" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6">
        <div className="bg-red-500/10 border border-red-500/40 rounded-xl p-6 max-w-md text-center" data-testid="parte-error">
          <IconX className="w-10 h-10 text-red-400 mx-auto mb-2" />
          <div className="text-red-200 font-bold mb-1">Parte no disponible</div>
          <div className="text-zinc-400 text-sm">{error}</div>
        </div>
      </div>
    );
  }

  const cliente = data.cliente || {};
  const peticion = data.peticion || {};
  const parte = data.parte || {};
  const yaFirmado = parte.estado === "firmado";

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 px-4 py-6" data-testid="parte-portal">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          {cliente.logo_url && <img src={cliente.logo_url} alt={cliente.nombre} className="w-12 h-12 rounded-full object-contain bg-white p-1" />}
          <div className="flex-1">
            <div className="text-zinc-300 text-sm">{cliente.nombre || "Cliente"}</div>
            <div className="text-zinc-500 text-xs font-mono">{peticion.codigo}</div>
          </div>
          <img src="/logo-dnf.png" alt="DNF" className="h-10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        </div>

        {/* Card */}
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-5 mb-4">
          <div className="text-amber-300 font-bold flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4" /> Parte de Montaje
          </div>
          <p className="text-sm text-zinc-400">
            {peticion.nombre || "Trabajo de montaje completado"}
            {peticion.fecha_montaje && <span> · Fecha: <b className="text-zinc-300">{peticion.fecha_montaje}</b></span>}
          </p>
          {peticion.direccion && <p className="text-xs text-zinc-500 mt-1">📍 {peticion.direccion}</p>}

          <div className="mt-4 flex flex-wrap gap-2">
            {parte.pdf_preview_url && !yaFirmado && (
              <a href={parte.pdf_preview_url} target="_blank" rel="noreferrer" data-testid="link-pdf-preview">
                <Button size="sm" variant="outline" className="text-xs">
                  <Download className="w-3 h-3 mr-1" /> Ver parte (PDF)
                </Button>
              </a>
            )}
            {parte.pdf_firmado_url && (
              <a href={parte.pdf_firmado_url} target="_blank" rel="noreferrer" data-testid="link-pdf-firmado">
                <Button size="sm" className="text-xs bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-bold">
                  <Download className="w-3 h-3 mr-1" /> Descargar parte firmado
                </Button>
              </a>
            )}
          </div>
        </div>

        {/* Ya firmado */}
        {yaFirmado && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-2xl p-6 text-center" data-testid="parte-firmado">
            <CheckCircle2 className="w-16 h-16 mx-auto text-emerald-400 mb-3" />
            <div className="text-emerald-300 font-bold text-xl mb-1">Parte firmado</div>
            <div className="text-zinc-400 text-sm">
              Firmado por <b className="text-zinc-200">{parte.firmado_por}</b>
              {parte.firmado_dni && <> ({parte.firmado_dni})</>}
              {parte.firmado_at && <> el {parte.firmado_at.slice(0, 10)}</>}
            </div>
            <div className="text-zinc-500 text-xs mt-3 flex items-center justify-center gap-1">
              <Heart className="w-3 h-3 fill-pink-400 text-pink-400" /> Gracias por confiar en RotulaciónDNF
            </div>
          </div>
        )}

        {/* Pendiente firma */}
        {!yaFirmado && (
          <div className="bg-zinc-900/60 border border-amber-500/40 rounded-2xl p-5 space-y-5" data-testid="parte-form">
            <div>
              <div className="text-amber-300 font-bold text-base flex items-center gap-2">
                <PenLine className="w-4 h-4" /> Firma tu conformidad
              </div>
              <p className="text-xs text-zinc-400 mt-1">Al firmar confirmas que el montaje ha sido realizado correctamente y que das tu conformidad al trabajo entregado.</p>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs text-zinc-400">Nombre y apellidos *</Label>
                <Input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Quien firma en representación del cliente"
                  className="bg-zinc-950 border-zinc-700 mt-1"
                  data-testid="parte-input-nombre"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">DNI/NIF <span className="text-zinc-600">(opcional, recomendado)</span></Label>
                <Input
                  value={dni}
                  onChange={(e) => setDni(e.target.value)}
                  placeholder="12345678X"
                  className="bg-zinc-950 border-zinc-700 mt-1 font-mono"
                  data-testid="parte-input-dni"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-zinc-400">Firma con el dedo aquí *</Label>
                <Button variant="ghost" size="sm" onClick={clearSig} className="text-xs h-7" data-testid="parte-btn-limpiar">
                  <Trash2 className="w-3 h-3 mr-1" /> Borrar
                </Button>
              </div>
              <div className="bg-white rounded-lg overflow-hidden border-2 border-amber-500/40 touch-none">
                <SignatureCanvas
                  ref={sigRef}
                  canvasProps={{
                    className: "w-full h-48",
                    "data-testid": "parte-signature-canvas",
                  }}
                  penColor="#1f2937"
                  minWidth={1.5}
                  maxWidth={3}
                />
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 text-center">Usa el dedo (móvil) o el ratón (PC) para firmar dentro del recuadro blanco</div>
            </div>

            <Button
              onClick={submit}
              disabled={saving}
              data-testid="parte-btn-firmar"
              className="w-full bg-gradient-to-r from-amber-500 to-emerald-500 hover:from-amber-600 hover:to-emerald-600 text-zinc-900 font-bold py-6 text-base"
            >
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <PenLine className="w-5 h-5 mr-2" />}
              Firmar conformidad
            </Button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-zinc-600 space-y-1">
          <div>RotulaciónDNF · Producción Gráfica</div>
          <div>☎ +34 624 49 12 71 · <a href="https://rotulaciondnf.com" className="underline">rotulaciondnf.com</a></div>
        </div>
      </div>
    </div>
  );
}
